import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  RefreshCw, Loader2, ChevronLeft, ChevronRight, Users, XCircle,
  CheckCircle2, BookOpen, UserCheck, Filter, Phone, Repeat, Sparkles, MapPin,
  Copy, AlertTriangle, MessageSquare,
} from 'lucide-react';
import { PlaybookModal, ReassignModal } from './RevivableDealQueue';
import RevivableDealQueue from './RevivableDealQueue';
import { DateRangePills } from './AnalyticsFilters';
import { RepliesInboxCard } from './RepliesInboxCard';
import { SmsScorecardCard } from './SmsScorecardCard';
import { MorningCallsCard } from './MorningCallsCard';
import { canSendSms } from '../../pages/call-detail/CoachingPlaybookCard';
import DigestPreviewCard from '../../pages/admin/overview/DigestPreviewCard';
import WonPill from '../WonPill';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 100;

const DISC_COLOR = { D: '#EF4444', I: '#F59E0B', S: '#10B981', C: '#3B82F6' };

const SORT_OPTIONS = [
  { value: 'latest_call',  label: 'Freshness (newest first)' },
  { value: 'confidence',   label: 'Confidence (recency-weighted)' },
  { value: 'call_date',    label: 'Age (oldest first)' },
  { value: 'graded_date',  label: 'Graded date (oldest first)' },
];

const _fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso.slice(0, 10); }
};

const _ageLabel = (d) => {
  if (d == null) return '—';
  if (d === 0) return 'today';
  if (d === 1) return '1d';
  if (d < 7) return `${d}d`;
  if (d < 30) return `${Math.round(d / 7)}w`;
  return `${Math.round(d / 30)}mo`;
};

const _confColor = (c) => (c >= 60 ? '#10B981' : c >= 35 ? '#F59E0B' : '#EF4444');

/**
 * GlobalRevivableDealsTab — a full-width paginated table of every revivable
 * deal in the tenant. Admin & supervisor only. Supports sorting, bulk
 * actions, contacted-follow-through auditing, and multi-contact filtering.
 */
const GlobalRevivableDealsTab = ({ domainId = null }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [sortBy, setSortBy] = useState('latest_call');
  const [siteSort, setSiteSort] = useState(false);
  const [includeContacted, setIncludeContacted] = useState(false);
  const [multiContactOnly, setMultiContactOnly] = useState(false);
  const [siteFilter, setSiteFilter] = useState('');
  const [sitesAvailable, setSitesAvailable] = useState([]);
  const [termDefault, setTermDefault] = useState('Opp');
  const [excludeVoicemail, setExcludeVoicemail] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [playbookFor, setPlaybookFor] = useState(null);
  const [reassignFor, setReassignFor] = useState(null);
  const [reassignBulk, setReassignBulk] = useState(false);
  const [attributionFlags, setAttributionFlags] = useState({});
  const [days, setDays] = useState(7); // page-level date filter, default 7d
  const [agentFilter, setAgentFilter] = useState('');
  const [agentOptions, setAgentOptions] = useState([]);
  const daysParam = days == null ? 'all' : String(days);
  const isAgentRole = !['master_admin', 'domain_admin', 'manager', 'admin', 'domain_supervisor', 'supervisor'].includes(user?.role);

  // Agent-filter options (managers only — a no-op for MCs, who are
  // already RBAC-scoped to their own deals server-side).
  useEffect(() => {
    if (isAgentRole) return;
    const p = domainId ? `?domain_id=${domainId}` : '';
    axios.get(`${API}/analytics/team${p}`, { timeout: 30000 })
      .then((r) => setAgentOptions(r.data?.leaderboard || []))
      .catch(() => setAgentOptions([]));
  }, [domainId, isAgentRole]);

  // Fetch open attribution flags for the currently visible rows so the
  // Row component can render a small amber warning pill next to each
  // agent whose call has a mismatched speaker. One batch request, no
  // N+1 waterfalls.
  useEffect(() => {
    if (!rows || rows.length === 0) {
      setAttributionFlags({});
      return;
    }
    const call_ids = rows.map((r) => r.call_id).filter(Boolean);
    axios
      .post(`${API}/calls/attribution-flags/batch`, { call_ids }, { timeout: 12000 })
      .then((r) => setAttributionFlags(r.data?.flags || {}))
      .catch(() => setAttributionFlags({}));
  }, [rows]);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    const controller = opts.controller || new AbortController();
    try {
      const params = new URLSearchParams({
        days: daysParam,
        limit: String(PAGE_SIZE),
        skip: String(skip),
        // The "Sort by site" tickbox is a hard override — when checked
        // it forces the backend to A→Z regardless of the dropdown.
        sort_by: siteSort ? 'site' : sortBy,
        include_contacted: String(includeContacted),
        min_contacts: multiContactOnly ? '2' : '0',
        exclude_voicemail: String(excludeVoicemail),
        paginated: 'true',
      });
      if (siteFilter) params.set('site', siteFilter);
      if (agentFilter) params.set('agent_id', agentFilter);
      if (domainId) params.set('domain_id', domainId);
      const r = await axios.get(
        `${API}/analytics/deal-outcomes/revivable-queue?${params}`,
        { signal: controller.signal },
      );
      // Belt-and-braces guard — even if axios doesn't reject on abort
      // (narrow window between response arrival + abort call) we still
      // drop the payload so a stale fetch can't clobber the fresh one.
      if (controller.signal.aborted) return controller;
      setRows(r.data?.queue || []);
      setTotal(r.data?.total || 0);
      setHasMore(!!r.data?.has_more);
      if (r.data?.sites_available) setSitesAvailable(r.data.sites_available);
      if (r.data?.term_default) setTermDefault(r.data.term_default);
      setSelected(new Set());
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || controller.signal.aborted) return controller;
      toast.error(e?.response?.data?.detail || 'Could not load revivable opportunities');
      setRows([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
    return controller;
  }, [skip, sortBy, siteSort, includeContacted, multiContactOnly, excludeVoicemail, siteFilter, domainId, daysParam, agentFilter]);

  useEffect(() => {
    const controller = new AbortController();
    load({ controller });
    return () => { controller.abort(); };
  }, [load]);

  // Reset pagination whenever the filter or sort changes.
  useEffect(() => { setSkip(0); }, [sortBy, siteSort, includeContacted, multiContactOnly, siteFilter, daysParam, agentFilter]);

  // Deep-link support — read ?site= from the URL and auto-apply the chip.
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('site');
      if (q) setSiteFilter(q);
    } catch { /* ignore */ }
  }, []);

  const page = useMemo(() => Math.floor(skip / PAGE_SIZE) + 1, [skip]);
  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const toggleRow = (call_id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(call_id)) next.delete(call_id); else next.add(call_id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.call_id)));
  };

  const runBulk = async (action, extra = {}) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/analytics/deal-outcomes/bulk-action`, {
        call_ids: Array.from(selected),
        action,
        ...extra,
      });
      const ok = r.data?.ok || 0;
      const totalReq = r.data?.total || selected.size;
      if (ok === totalReq) toast.success(`${action.replace('_', ' ')} on all ${ok} deals ✓`);
      else toast.warning(`${action.replace('_', ' ')} on ${ok}/${totalReq} deals — check log`);
      setSelected(new Set());
      setReassignBulk(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  // Wrap the ReassignModal onDone for bulk usage — one agent for all selected.
  const handleBulkReassignPick = ({ agent_id, note }) => {
    if (!agent_id) return;
    runBulk('reassign', { new_agent_id: agent_id, note: note || null });
  };

  /**
   * Bulk-send the LLM-drafted pre-call SMS to the currently selected
   * revivable callers. Fetches each caller's cached playbook (which
   * has the per-caller `pre_call_sms` text) then hits the backend
   * bulk endpoint. Skips selected rows where the playbook is missing
   * or has no SMS suggestion.
   */
  const bulkSendSms = async () => {
    if (selected.size === 0) return;
    const simulated = window.confirm(
      `Bulk-send Yabbit SMS to ${selected.size} caller${selected.size === 1 ? '' : 's'}?\n\n` +
      `OK = TEST-FIRE (simulated, no cost, no real SMS)\n` +
      `Cancel = LIVE send`,
    ) ? true : false;
    // Second confirmation for live sends to prevent fat-finger.
    if (!simulated && !window.confirm(
      `LIVE SEND confirmed for ${selected.size} caller${selected.size === 1 ? '' : 's'}?\n\n` +
      `Each will receive the SMS drafted by their coaching playbook. This costs money.`,
    )) return;

    setBulkBusy(true);
    try {
      // Load each selected caller's playbook to get its pre_call_sms.
      // We do this client-side sequentially so we don't hammer the API.
      const items = [];
      const skipped = [];
      for (const call_id of selected) {
        try {
          const r = await axios.post(`${API}/analytics/revival/${call_id}/playbook`);
          const sms = (r.data?.pre_call_sms || '').trim();
          if (sms) items.push({ call_id, content: sms });
          else skipped.push({ call_id, reason: 'no SMS in playbook' });
        } catch (e) {
          skipped.push({ call_id, reason: e?.response?.data?.detail || 'playbook fetch failed' });
        }
      }
      if (items.length === 0) {
        toast.error(`No callers had a pre-call SMS drafted (skipped: ${skipped.length}).`);
        return;
      }
      const r = await axios.post(`${API}/analytics/revival/bulk-send-sms`, {
        items, via: 'yabbr', simulated,
      });
      const prefix = simulated ? 'Test-fire' : 'Sent';
      toast.success(`${prefix} ${r.data.sent_ok}/${r.data.total} SMS ✓ (${skipped.length} skipped)`);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk SMS failed');
    } finally {
      setBulkBusy(false);
    }
  };

  /**
   * Bulk-copy the current queue (or the selected subset) as a formatted
   * plain-text block a manager can paste into a stand-up doc / Slack /
   * email. Uses the ROWS the user is currently looking at — so filters,
   * sort, and page position are all respected.
   */
  const copyList = async () => {
    const source = selected.size > 0
      ? rows.filter((r) => selected.has(r.call_id))
      : rows;
    if (!source.length) {
      toast.error('Nothing to copy');
      return;
    }
    const sortLabel = siteSort
      ? 'Site (A → Z)'
      : (SORT_OPTIONS.find((o) => o.value === sortBy)?.label || sortBy);
    const filters = [
      `sort: ${sortLabel}`,
      siteFilter ? `site: ${siteFilter}` : null,
      multiContactOnly ? 'contacted more than once' : null,
      includeContacted ? 'incl. already-contacted' : null,
    ].filter(Boolean).join(' · ');

    const now = new Date().toLocaleString(undefined, {
      dateStyle: 'medium', timeStyle: 'short',
    });
    const origin = window.location.origin;
    const lines = [];
    lines.push('═════════════════════════════════════════════════════');
    lines.push('  YABBIT VOICEFABRIC — REVIVABLE OPPORTUNITY QUEUE');
    lines.push('═════════════════════════════════════════════════════');
    lines.push(`Exported: ${now}   ·   ${source.length} deal${source.length === 1 ? '' : 's'}`);
    if (filters) lines.push(`Filters: ${filters}`);
    lines.push('');

    source.forEach((r, i) => {
      const rank = String(i + 1).padStart(2, ' ');
      const agent = r.agent_name || `ext ${r.agent_id || '—'}`;
      const phone = r.customer_phone || r.caller_phone || '—';
      const conf = r.confidence != null ? `${Math.round(r.confidence)}%` : '—';
      const ageDays = r.age_days ?? r.days_since_call ?? null;
      const ageStr = ageDays == null ? '—'
        : ageDays === 0 ? 'today'
        : ageDays === 1 ? '1 day ago'
        : ageDays < 7 ? `${ageDays} days ago`
        : ageDays < 30 ? `${Math.round(ageDays / 7)} weeks ago`
        : `${Math.round(ageDays / 30)} months ago`;
      const lastCall = r.last_call_date || r.call_date;
      const lastCallStr = lastCall
        ? new Date(lastCall).toLocaleDateString(undefined,
          { day: '2-digit', month: 'short', year: '2-digit' })
        : '—';
      const score = r.overall_score ?? r.last_score;
      const disc = r.caller_disc || null;
      const site = r.site_name || r.site || null;
      const contactedTimes = r.contact_count || r.times_contacted || 0;

      lines.push(`${rank}. ${agent}  →  ${phone}`);
      const meta = [
        `confidence ${conf}`,
        `age ${ageStr}`,
        `last call ${lastCallStr}`,
        score != null ? `score ${score}/100` : null,
        disc ? `DISC ${disc}` : null,
        site ? `site ${site}` : null,
        contactedTimes > 0 ? `contacted ${contactedTimes}×` : null,
      ].filter(Boolean).join(' · ');
      lines.push(`     ${meta}`);
      const reason = r.reason || r.ai_summary || r.revival_hint;
      if (reason) {
        const trimmed = String(reason).trim().slice(0, 220);
        lines.push(`     Why: ${trimmed}${String(reason).length > 220 ? '…' : ''}`);
      }
      lines.push(`     Open: ${origin}/calls/${r.call_id}`);
      lines.push('');
    });

    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${source.length} deal${source.length === 1 ? '' : 's'} to clipboard`);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Copied to clipboard');
      } catch {
        toast.error('Copy failed — browser blocked clipboard access');
      }
    }
  };


  const contactedCount = rows.filter((r) => r.contacted_at).length;
  const isManager = ['master_admin','domain_admin','manager','admin','domain_supervisor','supervisor'].includes(user?.role);

  // ── MC (agent) view — permission-trimmed version of this tab: same
  // queue columns but a single view-only Best-Fit hint, no bulk actions,
  // no reallocation. Server-side RBAC already scopes rows to the agent.
  if (!isManager) {
    return (
      <div className="space-y-4" data-testid="agent-revivable-tab">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <RefreshCw className="w-5 h-5 text-teal-600" /> Revivable Deals — yours
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Every revivable deal assigned to you from the last 60 days that hasn't been dealt with. The Best-fit column is a hint — only supervisors can reallocate deals.
          </p>
        </div>
        <MorningCallsCard />
        <RepliesInboxCard />
        <div className="flex items-center justify-between gap-4 flex-wrap bg-white border border-slate-200 rounded-xl px-4 py-2.5" data-testid="agent-revivable-filters">
          <span className="text-xs font-semibold text-slate-500" data-testid="agent-revivable-window-label">Showing the last 60 days</span>
          <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-slate-700" data-testid="agent-revivable-activity-filter" title="Activity needed = deals you haven't called back yet. Tick to also show deals you've already contacted.">
            <input
              type="checkbox"
              checked={includeContacted}
              onChange={(e) => setIncludeContacted(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-400"
            />
            Show contacted too (default: activity needed only)
          </label>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>
        ) : (
          <RevivableDealQueue
            agentMode
            queue={rows}
            domainId={domainId}
            termDefault={termDefault}
            onRefresh={(sb) => { if (sb) setSortBy(sb); load(); }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="global-revivable-tab">
      <MorningCallsCard />
      <RepliesInboxCard />
      {isManager && <SmsScorecardCard />}
      {isManager && (
        <DigestPreviewCard defaultDomainId={domainId} />
      )}

      {/* Per-site chip drill-down. Chips are derived from the tenant's
          extension_sites map so they don't disappear when the current
          filter empties the view. Clicking "All" resets the site filter. */}
      {sitesAvailable.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="site-chip-row">
          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mr-1">Site</span>
          <button
            type="button"
            onClick={() => setSiteFilter('')}
            className={`text-xs font-semibold px-3 py-1 rounded-full border transition ${
              siteFilter === ''
                ? 'bg-teal-600 text-white border-teal-600'
                : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
            }`}
            data-testid="site-chip-all"
          >All</button>
          {sitesAvailable.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSiteFilter(s)}
              className={`text-xs font-semibold px-3 py-1 rounded-full border transition ${
                siteFilter === s
                  ? 'bg-teal-600 text-white border-teal-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
              }`}
              data-testid={`site-chip-${s}`}
            >{s}</button>
          ))}
        </div>
      )}

      {/* Header + filter bar */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <RefreshCw className="w-5 h-5 text-teal-600" /> Revivable Opportunity Queue — all
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Every revivable opportunity across the tenant. Sort, filter, action in bulk.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => copyList()}
            disabled={loading || rows.length === 0}
            className="text-xs font-semibold text-slate-600 border border-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="global-revivable-copy"
            title={selected.size > 0
              ? `Copy the ${selected.size} selected deal${selected.size === 1 ? '' : 's'} to clipboard`
              : `Copy all ${rows.length} deal${rows.length === 1 ? '' : 's'} on this page to clipboard`}
          >
            <Copy className="w-3.5 h-3.5" />
            {selected.size > 0 ? `Copy selected (${selected.size})` : `Copy list${rows.length > 0 ? ` (${rows.length})` : ''}`}
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs font-semibold text-slate-600 border border-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="global-revivable-refresh"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Top-3 Best-Fit queue — moved here from the Intelligence tab
          (Sep-2026 spec). The Top-3 chips are actionable: clicking a name
          opens the reassign flow pre-filled with that agent. */}
      <RevivableDealQueue
        queue={rows.slice(0, 25)}
        domainId={domainId}
        termDefault={termDefault}
        onRefresh={(sb) => { if (sb) setSortBy(sb); load(); }}
      />

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-3 text-xs">
        <DateRangePills days={days} onChange={setDays} includeAll={false} />
        <div className="flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 text-slate-500" />
          <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Agent</span>
          <select
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
            data-testid="global-revivable-agent-filter"
            title="Filter the queue to deals owned by (or reassigned to) one agent"
          >
            <option value="">All agents</option>
            {agentOptions.map((a) => (
              <option key={a.agent_id} value={a.agent_id}>{a.agent_name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Sort</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            disabled={siteSort}
            className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400 disabled:opacity-40"
            data-testid="global-revivable-sort"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="global-revivable-toggle-site">
          <input
            type="checkbox"
            checked={siteSort}
            onChange={(e) => setSiteSort(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-400"
          />
          <span className="text-xs font-semibold text-slate-700">
            <MapPin className="w-3 h-3 inline-block mr-0.5" />
            Sort by site (A → Z)
          </span>
        </label>

        <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="global-revivable-toggle-contacted">
          <input
            type="checkbox"
            checked={includeContacted}
            onChange={(e) => setIncludeContacted(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-400"
          />
          <span className="text-xs font-semibold text-slate-700">Include contacted (audit follow-through)</span>
        </label>

        <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="global-revivable-toggle-multi">
          <input
            type="checkbox"
            checked={multiContactOnly}
            onChange={(e) => setMultiContactOnly(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-400"
          />
          <span className="text-xs font-semibold text-slate-700">
            <Repeat className="w-3 h-3 inline-block mr-0.5" />
            Only callers contacted more than once
          </span>
        </label>

        <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="global-revivable-toggle-voicemail">
          <input
            type="checkbox"
            checked={excludeVoicemail}
            onChange={(e) => setExcludeVoicemail(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-400"
          />
          <span className="text-xs font-semibold text-slate-700">
            Hide voicemails
          </span>
        </label>

        <div className="ml-auto text-slate-500 tabular-nums text-[11px]">
          {loading ? 'loading…' : (
            <>
              <span className="font-semibold text-slate-800">{total.toLocaleString()}</span> deal{total === 1 ? '' : 's'}
              {includeContacted && contactedCount > 0 && (
                <> · <span className="text-emerald-600 font-semibold">{contactedCount} on this page contacted</span></>
              )}
            </>
          )}
        </div>
      </div>

      {/* Bulk-action bar */}
      {selected.size > 0 && (
        <div
          className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-2.5 flex items-center justify-between gap-3 text-xs"
          data-testid="bulk-action-bar"
        >
          <span className="font-semibold text-teal-900">
            {selected.size} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setReassignBulk(true)}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
              data-testid="bulk-reassign-btn"
            >
              <UserCheck className="w-3.5 h-3.5" /> Bulk re-assign
            </button>
            <button
              onClick={() => runBulk('mark_lost')}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
              data-testid="bulk-mark-lost-btn"
            >
              <XCircle className="w-3.5 h-3.5" /> Bulk mark lost
            </button>
            <button
              onClick={() => runBulk('mark_contacted')}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
              data-testid="bulk-mark-contacted-btn"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Bulk mark contacted
            </button>
            {canSendSms(user) && (
              <button
                onClick={bulkSendSms}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
                data-testid="bulk-send-sms-btn"
                title="Send each selected caller's LLM-drafted pre-call SMS via Yabbit SMS. Opens a test-fire/live confirm."
              >
                <MessageSquare className="w-3.5 h-3.5" /> Bulk send SMS
              </button>
            )}
            <button
              onClick={() => setSelected(new Set())}
              className="text-slate-500 hover:text-slate-800 text-xs font-semibold px-2"
            >Clear</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading revivable opportunities…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm">
            No revivable opportunities match the current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs" data-testid="global-revivable-table">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="px-3 py-2 text-left w-8">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && selected.size === rows.length}
                      onChange={toggleAll}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-400"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Caller</th>
                  <th className="px-3 py-2 text-left">DISC</th>
                  <th className="px-3 py-2 text-left">Owned by</th>
                  <th className="px-3 py-2 text-left">Suggested</th>
                  <th className="px-3 py-2 text-right">Conf</th>
                  <th className="px-3 py-2 text-right">Age</th>
                  <th className="px-3 py-2 text-left">Activity</th>
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <Row
                    key={row.call_id}
                    row={row}
                    checked={selected.has(row.call_id)}
                    onToggle={() => toggleRow(row.call_id)}
                    onPlaybook={() => setPlaybookFor(row)}
                    onReassign={() => setReassignFor(row)}
                    onAfterAction={load}
                    attributionFlag={attributionFlags[row.call_id]}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {(pageCount > 1 || hasMore) && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div>Page <span className="font-semibold text-slate-800 tabular-nums">{page}</span> of <span className="tabular-nums">{pageCount}</span></div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              disabled={skip === 0 || loading}
              className="inline-flex items-center gap-1 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40 font-semibold"
              data-testid="page-prev"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Prev
            </button>
            <button
              onClick={() => setSkip(skip + PAGE_SIZE)}
              disabled={!hasMore || loading}
              className="inline-flex items-center gap-1 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40 font-semibold"
              data-testid="page-next"
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {playbookFor && (
        <PlaybookModal row={playbookFor} onClose={() => setPlaybookFor(null)} />
      )}
      {reassignFor && (
        <ReassignModal
          row={reassignFor}
          domainId={domainId}
          onClose={() => setReassignFor(null)}
          onDone={() => { setReassignFor(null); load(); }}
        />
      )}
      {reassignBulk && (
        <BulkReassignModal
          count={selected.size}
          domainId={domainId}
          busy={bulkBusy}
          onClose={() => setReassignBulk(false)}
          onSubmit={handleBulkReassignPick}
        />
      )}
    </div>
  );
};

const Row = ({ row, checked, onToggle, onPlaybook, onReassign, onAfterAction, attributionFlag }) => {
  const [busy, setBusy] = useState(null);
  const disc = row.caller_disc;

  const act = async (endpoint, label) => {
    setBusy(label);
    try {
      await axios.post(`${API}/analytics/revival/${row.call_id}/${endpoint}`);
      toast.success(`${label} ✓`);
      onAfterAction?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${label}`);
    } finally { setBusy(null); }
  };

  return (
    <tr className={`${checked ? 'bg-teal-50/50' : 'hover:bg-slate-50/60'} align-top`} data-testid={`row-${row.call_id}`}>
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-400"
          data-testid={`row-checkbox-${row.call_id}`}
        />
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <Link to={`/calls/${row.call_id}`} className="font-semibold text-slate-800 hover:text-teal-600 inline-flex items-center gap-1">
          <Phone className="w-3 h-3 text-slate-400" />
          {row.customer_phone || '—'}
        </Link>
        {row.chase_outcome === 'won' && (
          <div className="mt-1"><WonPill source={row.chase_outcome_source} by={row.chase_outcome_by} /></div>
        )}
        {row.total_call_count > 1 && (
          <div className="text-[10px] text-slate-500 mt-0.5">
            <Repeat className="w-2.5 h-2.5 inline-block mr-0.5" />
            {row.total_call_count} contacts total
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        {disc ? (
          <span
            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-[11px] font-bold"
            style={{ background: DISC_COLOR[disc] || '#94A3B8' }}
            title={`DISC · ${disc}`}
          >{disc}</span>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-3 py-2">
        <div className="text-slate-800 font-medium flex items-center gap-1">
          <span>{row.original_agent_name || '—'}</span>
          {attributionFlag && (
            <span
              className="inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1 py-[1px] cursor-help"
              title={`Attribution flag — transcript says "${attributionFlag.detected_name}". Suggested: ${attributionFlag.suggested_agent_name || 'no directory match'}. Click the call to review.`}
              data-testid={`revivable-attribution-flag-${row.call_id}`}
            >
              <AlertTriangle className="w-2.5 h-2.5" />
              MIS-ATTR
            </span>
          )}
        </div>
        {row.site && <div className="text-[10px] text-slate-400">{row.site}</div>}
      </td>
      <td className="px-3 py-2">
        <div className="text-slate-800 font-medium inline-flex items-center gap-1">
          {row.reassigned_by_manager && (
            <span className="text-[9px] font-bold text-teal-700 bg-teal-100 rounded px-1 py-[1px]">MGR</span>
          )}
          {row.strong_reassign && (
            <Sparkles className="w-3 h-3 text-amber-500" title={row.strong_reassign_reason} />
          )}
          {row.suggested_agent_name || '—'}
        </div>
      </td>
      <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: _confColor(row.confidence_score) }}>
        {Math.round(row.confidence_score || 0)}%
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{_ageLabel(row.age_days)}</td>
      <td className="px-3 py-2">
        <div className="text-[10px] text-slate-500">Called {_fmtDate(row.created_at)}</div>
        {row.subsequent_count > 0 && (
          <div
            className="text-[10px] font-bold text-amber-700 bg-amber-100 rounded px-1.5 py-[1px] inline-block mt-1"
            title="New calls between this caller and the tenant since the coached call — the playbook auto-refreshes on next open."
            data-testid={`row-subsequent-${row.call_id}`}
          >
            ↺ {row.subsequent_count} new call{row.subsequent_count === 1 ? '' : 's'} — playbook will re-merge
          </div>
        )}
        {row.contacted_at && (
          <div className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded px-1.5 py-[1px] inline-block mt-1">
            ✓ Contacted {_fmtDate(row.contacted_at)}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={onPlaybook}
            className="inline-flex items-center gap-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold px-2 py-1 rounded-md text-[11px]"
            title="Open the DISC-tuned callback playbook"
            data-testid={`row-playbook-${row.call_id}`}
          >
            <BookOpen className="w-3 h-3" /> Playbook
          </button>
          {!row.contacted_at && (
            <button
              onClick={() => act('mark-contacted', 'Marked contacted')}
              disabled={busy}
              className="inline-flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold px-2 py-1 rounded-md text-[11px] disabled:opacity-50"
              data-testid={`row-contacted-${row.call_id}`}
            >
              {busy === 'Marked contacted' ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
              Called
            </button>
          )}
          <button
            onClick={() => act('mark-won', 'Marked won')}
            disabled={busy}
            className="inline-flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold px-2 py-1 rounded-md text-[11px] disabled:opacity-50"
            data-testid={`row-won-${row.call_id}`}
          >
            Won
          </button>
          <button
            onClick={() => act('mark-lost', 'Marked lost')}
            disabled={busy}
            className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-2 py-1 rounded-md text-[11px] disabled:opacity-50"
            data-testid={`row-lost-${row.call_id}`}
          >
            Lost
          </button>
          <button
            onClick={onReassign}
            className="inline-flex items-center gap-1 bg-teal-50 hover:bg-teal-100 text-teal-700 font-semibold px-2 py-1 rounded-md text-[11px]"
            data-testid={`row-reassign-${row.call_id}`}
          >
            <Users className="w-3 h-3" /> Re-assign
          </button>
        </div>
      </td>
    </tr>
  );
};

const BulkReassignModal = ({ count, domainId, busy, onClose, onSubmit }) => {
  const [peers, setPeers] = useState([]);
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const q = domainId ? `?domain_id=${domainId}` : '';
        const r = await axios.get(`${API}/gamification/peers${q}`);
        if (!cancelled) setPeers(r.data?.peers || []);
      } catch { if (!cancelled) setPeers([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [domainId]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="bulk-reassign-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white px-5 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-100">
            Bulk re-allocate {count} opportunit{count === 1 ? 'y' : 'ies'}
          </p>
          <p className="text-xs text-teal-100 mt-0.5">
            All selected rows will be handed to the same agent.
          </p>
        </div>
        <div className="p-5 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
              <Loader2 className="w-3 h-3 animate-spin" /> loading peers…
            </div>
          ) : (
            <select
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
              data-testid="bulk-reassign-select"
            >
              <option value="">— pick an agent —</option>
              {peers.map((p) => (
                <option key={p.agent_id} value={p.agent_id}>{p.agent_name}</option>
              ))}
            </select>
          )}
          <textarea
            className="w-full text-sm border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none"
            rows={3}
            placeholder='Optional note applied to every row'
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={280}
            data-testid="bulk-reassign-note"
          />
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="text-xs font-semibold text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-100"
            >Cancel</button>
            <button
              type="button"
              onClick={() => to && onSubmit({ agent_id: to, note })}
              disabled={busy || !to}
              className="text-xs font-semibold bg-teal-600 text-white px-3 py-1.5 rounded-lg hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="bulk-reassign-submit"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
              Confirm bulk re-allocate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalRevivableDealsTab;
